import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import {
  usePermissionsList,
  useRolePermissions,
  useSetRolePermissions,
} from '../../api/queries';
import type { RoleSummary } from '../../api/types';

export type EditRolePermissionsDialogProps = {
  role: RoleSummary | null;
  onClose: () => void;
};

/**
 * Permission picker for a custom role. Floor permissions are pre-checked
 * and locked (the API rejects PUTs that drop them anyway). All other
 * permissions are toggleable. Save sends the full set including the
 * floor IDs so the API's "must include floor" guard passes.
 */
export function EditRolePermissionsDialog({ role, onClose }: EditRolePermissionsDialogProps) {
  const open = role !== null;
  const rolePermsQ = useRolePermissions(role?.id ?? null);
  const cataloguQ = usePermissionsList();
  const catalogue = useQuery({ queryKey: cataloguQ.queryKey, queryFn: cataloguQ.listFn });
  const setPerms = useSetRolePermissions(role?.id ?? '00000000-0000-0000-0000-000000000000');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');

  // Seed local state once both the role's current perms and the catalogue load.
  useEffect(() => {
    if (rolePermsQ.data) {
      setSelected(new Set(rolePermsQ.data.map((rp) => rp.permission_id)));
    }
  }, [rolePermsQ.data]);

  const floorIds = useMemo(
    () => new Set((rolePermsQ.data ?? []).filter((rp) => rp.is_floor).map((rp) => rp.permission_id)),
    [rolePermsQ.data],
  );

  // SystemMaster is the only is_system role; its editor is read-only and
  // shows the entire catalogue (so the admin can see what's locked in).
  const isSystem = role?.isSystem ?? false;

  const grouped = useMemo(() => {
    if (!catalogue.data) return null;
    const f = filter.trim().toLowerCase();
    // #161 — scope the editor to permissions whose tier-set contains this
    // role's tier. SystemMaster (and any other read-only view) shows the
    // entire catalogue so the admin can see what's locked in.
    const tierFiltered = role && !isSystem
      ? catalogue.data.filter((p) => (p.tiers ?? []).includes(role.tier))
      : catalogue.data;
    const filtered = !f ? tierFiltered : tierFiltered.filter((p) =>
      p.code.toLowerCase().includes(f) ||
      p.displayName.toLowerCase().includes(f) ||
      (p.description ?? '').toLowerCase().includes(f),
    );
    const out = new Map<string, typeof catalogue.data>();
    for (const p of filtered) {
      const arr = out.get(p.category) ?? [];
      arr.push(p);
      out.set(p.category, arr);
    }
    for (const arr of out.values()) arr.sort((a, b) => a.code.localeCompare(b.code));
    return Array.from(out.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue.data, filter, role, isSystem]);

  const dirty = useMemo(() => {
    if (!rolePermsQ.data) return false;
    const original = new Set(rolePermsQ.data.map((rp) => rp.permission_id));
    if (original.size !== selected.size) return true;
    for (const id of original) if (!selected.has(id)) return true;
    return false;
  }, [rolePermsQ.data, selected]);

  const toggle = (id: string) => {
    if (floorIds.has(id)) return; // locked
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = () => {
    setPerms.mutate(Array.from(selected), { onSuccess: onClose });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Stack>
          <Typography variant="h6">Edit role permissions</Typography>
          {role && (
            <Typography variant="caption" color="text.secondary">
              <code>{role.code}</code> · {role.tier}
              {isSystem ? ' · SystemMaster (read-only)' : ''}
            </Typography>
          )}
        </Stack>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isSystem && (
          <Alert severity="info" sx={{ mb: 2 }}>
            SystemMaster's permission set is immutable — it always holds every active permission.
            To customise a Platform-tier role, edit one of the unlocked seeded roles (ACES, ACESEngineering, ACESSupport)
            or clone SystemMaster into a new tenant role from the Roles page.
          </Alert>
        )}

        <TextField
          size="small"
          fullWidth
          placeholder="Filter by code, name, or description…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ mb: 2 }}
        />

        {rolePermsQ.isLoading || catalogue.isLoading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={20} /><Typography>Loading…</Typography>
          </Stack>
        ) : !grouped ? null : grouped.length === 0 ? (
          <Typography color="text.secondary">No permissions match.</Typography>
        ) : (
          <Stack spacing={2}>
            {grouped.map(([category, perms]) => (
              <Box key={category}>
                <Typography variant="overline" color="text.secondary">
                  {category} · {perms.length}
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  rowGap: 0.25,
                }}>
                  {perms.map((p) => {
                    const checked = selected.has(p.id);
                    const locked = floorIds.has(p.id);
                    return (
                      <Tooltip key={p.id} title={p.description ?? ''} placement="top-start">
                        <FormControlLabel
                          sx={{ m: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={checked}
                              disabled={isSystem || locked}
                              onChange={() => toggle(p.id)}
                            />
                          }
                          label={
                            <Stack>
                              <Typography variant="body2" sx={{
                                fontFamily: 'monospace', fontSize: 12,
                                color: locked ? 'text.secondary' : 'text.primary',
                              }}>
                                {p.code}{locked ? ' (floor)' : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {p.displayName}
                              </Typography>
                            </Stack>
                          }
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Stack>
        )}

        {setPerms.error && (
          <ErrorAlert sx={{ mt: 2 }}>
            {(setPerms.error as { message?: string }).message ?? 'Save failed.'}
          </ErrorAlert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={setPerms.isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={isSystem || !dirty || setPerms.isPending}
        >
          {setPerms.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
